import React from 'react';
import { FieldTitleProps } from '../../../types/DesignSystem/FieldTitle';
import Typography from '../Typography/Typography';
import InfoTooltip from '../Tooltip/InfoTooltip';

const FieldTitle = React.forwardRef<HTMLDivElement, FieldTitleProps>(function FieldTitle({
  label,
  required = false,
  className = '',
  infoMessage = '',
  size = 'md',
  disabled = false,
  htmlFor,
  labelId,
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const variant = size === 'sm' ? 'sub-title-label' : 'title';

  /*
    The label element wraps the TEXT ONLY, and IS the type token — `Typography`
    renders `as="label"` rather than sitting inside one.

    Both halves of that matter. Clicking anywhere inside a `<label>` activates the
    control it names, so with the info tooltip inside it, clicking the "ⓘ" would
    focus the input as a side effect — and on a checkbox it would toggle it. Hence
    the required marker and the tooltip are siblings of the label, not children.
    And making the label itself the token, instead of nesting one more element,
    keeps the colour and size classes on the element that renders the label text,
    which is where anything reading this component expects to find them.
  */
  return (
    <div {...rest} ref={ref} className={className} data-testid={dataTestId}>
      {/*
        `ink-tertiary`, not `ink`: a field label is metadata, and the VALUE in the
        control below it is the content. At full ink and semibold the label was the
        darkest, heaviest thing on the surface — it outweighed the data it was
        labelling. The UI this was modelled on sets its label well below its value
        (#747591 against #090a3f), and the tertiary ink is the declared token for that
        job. (§ 383 — this named the palette step the role resolved to before § 253.)

        No `tw-font-semibold` either. It was overriding the variant it had just
        chosen: `title` is 14px/**500**, and forcing 600 on top made `md` a
        different weight from the token it names — if 14px/600 were wanted, that is
        exactly what the `title-header` token is. `sm` still lands on 12px/600 via
        `sub-title-label`, which gives the two tiers the source UI has: a semibold
        section header and a lighter, larger field label, sharing one colour.
      */}
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        <Typography
          variant={variant}
          as={htmlFor ? 'label' : 'span'}
          htmlFor={htmlFor}
          id={labelId}
          className={disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'}
          data-testid={dataTestId ? `${dataTestId}-label` : undefined}
        >
          {label}
        </Typography>
        {/*
          `aria-hidden`: read out, this is the word "asterisk", which tells a screen
          reader user nothing. Requiredness belongs on the CONTROL, as
          `aria-required` — `TextField` and `Dropdown` now set it. A caller using
          `FieldTitle` directly has to do the same on whatever it labels, or the
          information exists only for sighted users (WCAG 3.3.2).
        */}
        {/*
          The marker takes the LABEL'S OWN type token, not the ambient font.

          It was a bare `<span className="tw-text-tone-danger-ink">*</span>`. With no
          type class it inherited `font-size` from `body { font-size: .9rem }` — 14.4px
          — plus Tailwind's base `line-height: 1.5`, giving it a **21.6px** line box
          against the `title` token's 14px/**20px**. This row is `inline-flex
          items-center`, so the taller child set the height and every `required` field
          rendered its label row 1.61px taller than a non-required one — which pushes
          the control below it down by the same amount.

          Measured in headless Chromium against the built stylesheet on 2026-08-27
          (§ 112): it is why the Compatibility form's "Name" column sat 1.6px below
          "Rule Type" and "Asymmetric". 24 `required` fields across 11 files were
          affected. Nothing could have caught it in jsdom, which performs no layout.

          Reusing `variant` rather than hardcoding a leading means the marker cannot
          drift from the label again if the `size` → variant mapping changes.
        */}
        {required && (
          <Typography
            variant={variant}
            as="span"
            className="tw-text-tone-danger-ink"
            aria-hidden="true"
          >
            *
          </Typography>
        )}
        {infoMessage && <InfoTooltip message={infoMessage} placement="top" />}
      </span>
    </div>
  );
});

export default FieldTitle;